import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { parseApiUtc } from '@/utils/apiDate';
import { appDateTime, appMonthName } from '@/utils/appDateFormat';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  FileSpreadsheet,
  CheckCircle2,
  AlertTriangle,
  ShieldCheck,
  Clock,
  XCircle,
  ArrowRight,
  Info,
  ClipboardList,
} from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { PageLayout } from '../../../components/ui/PageLayout';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import Button from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';
import StatusBadge from '../../../components/ui/StatusBadge';
import { Card, CardContent } from '../../../components/ui/Card';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from '../../../components/ui/Dialog';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import YearMonthPicker from '../../../components/yearMonthPicker';
import { useComplianceCountry } from '../../../hooks/useComplianceCountry';
import {
  getWpsHistory,
  generateSif,
  markWpsSubmitted,
  getSifPreflight,
  getWpsDeadline,
  getKsaWpsHistory,
} from '../../../api/Payroll/wps';
import {
  WpsSifSubmissionView,
  SifPreflightView,
  WpsDeadlineView,
  KsaWpsSubmissionView,
  sifCheckFixPath,
  sifEmployeeIssueFixPath,
} from '../../../models/payroll';
import { formatNumber } from '../../../utils';
import { MohreTimeline } from '../ComplianceCenter/components/MohreTimeline';
import { MudadTimeline } from '../ComplianceCenter/components/MudadTimeline';
import { LawReference } from '../ComplianceCenter/components/LawReference';
import { MarkUploadedModal } from '../ComplianceCenter/components/MarkUploadedModal';
import { KsaMudadHistory } from '../ComplianceCenter/components/KsaMudadHistory';

// 'neutral', not 'default': StatusBadge has no 'default' variant (its cva defaultVariants is
// 'neutral'), so 'default' fell through to an undefined dotColors entry and an unstyled dot.
const statusVariant: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  Generated: 'neutral',
  Submitted: 'warning',
  Accepted: 'success',
  Rejected: 'error',
};

const zoneColors: Record<string, string> = {
  Green: 'bg-green-100 text-green-800 border-green-200',
  Amber: 'bg-amber-100 text-amber-800 border-amber-200',
  Red: 'bg-red-100 text-red-800 border-red-200',
  Overdue: 'bg-red-200 text-red-900 border-red-300',
};

const WpsCenter: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();
  const country = useComplianceCountry();
  const isKsa = country === 'KSA';
  const isUae = country === 'UAE';

  const today = new Date();
  const [yearMonth, setYearMonth] = useState(today);
  const [history, setHistory] = useState<WpsSifSubmissionView[]>([]);
  const [ksaHistory, setKsaHistory] = useState<KsaWpsSubmissionView[]>([]);
  const [preflight, setPreflight] = useState<SifPreflightView | null>(null);
  const [deadline, setDeadline] = useState<WpsDeadlineView | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [marking, setMarking] = useState<number | null>(null);
  const [markOpen, setMarkOpen] = useState(false);
  const [fileWarningOpen, setFileWarningOpen] = useState(false);
  const [preflightPopupOpen, setPreflightPopupOpen] = useState(false);

  const year = yearMonth.getFullYear();
  const month = yearMonth.getMonth() + 1;
  const monthName = useMemo(
    () => appMonthName(new Date(year, month - 1)),
    [year, month, i18n.language]
  );

  const loadHistory = useCallback(async () => {
    if (!isUae) return;
    try {
      const resp = await getWpsHistory();
      setHistory(resp.payload || []);
    } catch (e) {
      console.error(e);
      showToast(t('Failed to load SIF history'), 'error');
    }
  }, [isUae, t, showToast]);

  const loadKsaHistory = useCallback(async () => {
    if (!isKsa) return;
    try {
      const resp = await getKsaWpsHistory();
      if (resp.errors.length === 0) setKsaHistory(resp.payload ?? []);
    } catch {
      /* keep prior state */
    }
  }, [isKsa]);

  const loadPeriodInfo = useCallback(async () => {
    setLoading(true);
    try {
      const [pf, dl] = await Promise.all([getSifPreflight(year, month), getWpsDeadline(year, month)]);
      setPreflight(pf.payload || null);
      setDeadline(dl.payload || null);
    } catch (e) {
      console.error(e);
      showToast(t('Failed to load WPS status'), 'error');
    } finally {
      setLoading(false);
    }
  }, [year, month, t, showToast]);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { loadKsaHistory(); }, [loadKsaHistory]);
  useEffect(() => { loadPeriodInfo(); }, [loadPeriodInfo]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await generateSif(year, month);
      showToast(
        isKsa
          ? t('payroll.complianceCenter.ksaWps.generateMudadOk', 'Mudad file downloaded.')
          : t('SIF generated and downloaded'),
        'success'
      );
      if (isKsa) {
        setFileWarningOpen(true);
      }
      await loadHistory();
      await loadKsaHistory();
      await loadPeriodInfo();
    } catch (e) {
      const raw = e instanceof Error ? e.message : '';
      if (/ksaPayrollMissingForPeriod/i.test(raw)) {
        showToast(
          t(
            'payroll.complianceCenter.ksaWps.noPayrollError',
            'No payroll has been generated for {{month}} {{year}} yet. Create it under Payroll → Monthly Payroll first, then come back to generate the Mudad file.',
            { month: monthName, year },
          ),
          'error',
          12000,
        );
      } else {
        showToast(raw || t('SIF generation failed'), 'error');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleRedownload = async (h: WpsSifSubmissionView) => {
    try {
      await generateSif(h.year, h.month);
      showToast(t('SIF re-downloaded for {{month}}/{{year}}', { month: h.month, year: h.year }), 'success');
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('Re-download failed');
      showToast(msg, 'error');
    }
  };

  const handleMarkSubmitted = async (h: WpsSifSubmissionView) => {
    setMarking(h.id);
    try {
      await markWpsSubmitted(h.year, h.month);
      showToast(t('Marked as submitted'), 'success');
      await loadHistory();
      await loadPeriodInfo();
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('Failed to mark submitted');
      showToast(msg, 'error');
    } finally {
      setMarking(null);
    }
  };

  const scrollToIssues = useCallback(() => {
    document.getElementById('employee-issues')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const filteredHistory = useMemo(
    () => [...history].sort((a, b) => new Date(b.generatedOn).getTime() - new Date(a.generatedOn).getTime()),
    [history]
  );

  const latestKsaForPeriod = useMemo(
    () => ksaHistory
      .filter(h => h.year === year && h.month === month)
      .sort((a, b) => +new Date(b.generatedOn) - +new Date(a.generatedOn))[0],
    [ksaHistory, year, month]
  );

  // SifEmployeeIssue fields: userId, employeeName, issue, field, isWarning
  const warningIssues = preflight?.employeeIssues?.filter(e => e.isWarning) ?? [];
  const blockingIssues = preflight?.employeeIssues?.filter(e => !e.isWarning) ?? [];
  const ibanIssues = blockingIssues.filter(e => /iban/i.test(e.issue) || /iban/i.test(e.field)).length;
  const labourCardIssues = blockingIssues.filter(e => /labour|labor|mol/i.test(e.issue + ' ' + e.field)).length;
  const otherIssues = blockingIssues.length - ibanIssues - labourCardIssues;
  const companyChecksFailed = preflight !== null && !preflight.companyPassed;
  const companyFailCount = preflight?.companyChecks?.filter(c => !c.passed).length ?? 0;
  // No payroll generated for the period — generating the file would fail with a raw error.
  const noPayrollForPeriod = preflight !== null && preflight.employeesTotal === 0;

  const title = isKsa ? t('payroll.wps.titleKsa', 'WPS (Mudad)') : t('payroll.wps.titleUae', 'WPS');

  return (
    <PageLayout
      title={title}
      icon={FileSpreadsheet}
      accentColor="emerald"
      maxWidth="1400px"
      headerActions={
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm" iconName="ArrowLeft">
            <Link to="/payroll/table">{t('Back to Payroll Table')}</Link>
          </Button>
          <Button asChild variant="outline" size="sm" iconName="ExternalLink">
            <Link to="/payroll/compliance-center">{t('Compliance Center')}</Link>
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5" data-testid="wps-center">
        {/* Period selector + summary cards */}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <YearMonthPicker date={yearMonth} onChange={setYearMonth} isRtl={false} />
            <Button variant="outline" size="sm" iconName="RefreshCw" onClick={loadPeriodInfo} loading={loading}>
              {t('payroll.complianceCenter.wps.recheck', 'Re-run checks')}
            </Button>
            {preflight && (
              <Tooltip.Provider>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <Button variant="outline" size="xs" onClick={() => setPreflightPopupOpen(true)} aria-label={t('payroll.wps.viewChecklist', 'View pre-flight checklist')}>
                      <ClipboardList size={16} />
                    </Button>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content className="bg-card border border-border rounded-md px-2 py-1 shadow-md text-xs z-50" sideOffset={5}>
                      {t('payroll.wps.viewChecklist', 'View pre-flight checklist')}
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
            )}
          </div>
          <div className="flex flex-wrap gap-3" data-testid="wps-summary">
            <SummaryCard
              icon={ShieldCheck}
              tone={preflight?.canGenerate ? 'ok' : 'error'}
              label={isKsa ? t('payroll.wps.preflightKsa', 'Mudad Pre-flight') : t('SIF Pre-flight')}
              value={preflight ? (preflight.canGenerate ? t('Ready') : t('{{n}} issues', { n: preflight.employeeIssues?.length ?? 0 })) : '—'}
              testId="card-preflight"
            />
            <SummaryCard
              icon={ShieldCheck}
              tone={preflight ? (companyChecksFailed ? 'error' : 'ok') : 'ok'}
              label={t('payroll.wps.companySetup', 'Company Setup')}
              value={preflight ? (companyChecksFailed ? t('{{n}} issues', { n: companyFailCount }) : t('All passed')) : '—'}
              testId="card-company-setup"
            />
            <SummaryCard
              icon={AlertTriangle}
              tone={deadline?.zone === 'Green' ? 'ok' : deadline?.zone === 'Amber' ? 'warn' : 'error'}
              label={t('Deadline')}
              value={deadline ? `${deadline.daysRemaining}d ${deadline.zone}` : '—'}
              testId="card-deadline"
            />
            <SummaryCard
              icon={CheckCircle2}
              tone={preflight && preflight.employeesTotal > 0 && preflight.employeesPassed < preflight.employeesTotal ? 'error' : 'ok'}
              label={t('Employees Passing')}
              value={preflight ? `${preflight.employeesPassed}/${preflight.employeesTotal}` : '—'}
              testId="card-passing"
            />
          </div>
        </div>

        {/* Readiness alert — only surfaces when action is needed, so a red/amber state
            leads the page instead of getting lost among the green status cards/banners. */}
        {preflight && !noPayrollForPeriod && (blockingIssues.length > 0 || companyChecksFailed) && (
          <div
            className="rounded-xl border-2 border-red-300 bg-red-50 p-4 flex items-start justify-between gap-4 shadow-sm"
            data-testid="wps-blocking-banner"
          >
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-red-100 p-2 shrink-0">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="font-semibold text-red-800">
                  {t('payroll.wps.blockedTitle', '{{n}} issue(s) must be fixed before you can generate the {{file}} file', {
                    n: blockingIssues.length + companyFailCount,
                    file: isKsa ? 'Mudad' : 'SIF',
                  })}
                </p>
                <p className="text-sm text-red-700 mt-0.5">
                  {companyChecksFailed && blockingIssues.length === 0
                    ? t('payroll.wps.blockedSubtitleCompany', 'Company-level setup is incomplete. Scroll down to Company Setup Pre-Validation to fix.')
                    : t('payroll.wps.blockedSubtitle', 'Generation stays disabled until every employee passes the pre-flight checks below.')}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-red-300 text-red-700 hover:bg-red-100 shrink-0"
              onClick={scrollToIssues}
              iconName="ArrowDown"
            >
              {t('payroll.wps.reviewIssues', 'Review issues')}
            </Button>
          </div>
        )}
        {preflight && !noPayrollForPeriod && blockingIssues.length === 0 && !companyChecksFailed && warningIssues.length > 0 && (
          <div
            className="rounded-xl border border-amber-300 bg-amber-50 p-3 flex items-center gap-3"
            data-testid="wps-warning-banner"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800">
              {t('payroll.wps.warnBanner', 'Ready to generate. {{n}} coverage warning(s) to resolve before the bank deadline.', {
                n: warningIssues.length,
              })}
            </p>
          </div>
        )}

        <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="wps-regulator-note">
          <span>
            {t('payroll.wps.currency', 'Currency')}: <span className="font-medium">{isKsa ? 'SAR' : 'AED'}</span>
          </span>
          <Tooltip.Provider>
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <button type="button" className="text-muted-foreground hover:text-foreground" aria-label={isKsa ? 'SAMA' : 'CBUAE'}>
                  <Info size={14} />
                </button>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content className="bg-card border border-border rounded-md p-3 shadow-md text-xs max-w-xs z-50" sideOffset={5}>
                  {isKsa
                    ? t('payroll.wps.samaTooltip', 'Saudi Central Bank (SAMA) requires all salary payments to be denominated in Saudi Riyals (SAR) and processed through licensed banking channels under the Wage Protection System.')
                    : t('payroll.wps.cbuaeTooltip', 'Central Bank of the UAE (CBUAE) requires salary payments in AED through the Wages Protection System.')}
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          </Tooltip.Provider>
        </div>

        {/* Deadline section — neutral card with a colored zone badge instead of a
             full-width colored banner, so it doesn't drown out red/amber alerts above. */}
        {deadline && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-foreground">
                        {isKsa
                          ? (deadline.isRunPayrollMode
                            ? t('payroll.complianceCenter.wps.ksaRunPayrollTitle', 'Run Payroll: Mudad Clock Pending')
                            : t('payroll.complianceCenter.wps.ksaDeadlineStatus', 'Mudad Deadline Status'))
                          : t('payroll.wps.deadlineStatus', 'WPS Deadline Status')}
                      </p>
                      <LawReference
                        citations={[
                          isKsa
                            ? {
                                code: 'HRSD March 2025 announcement / KSA Labor Law Art. 90',
                                description: t('payroll.complianceCenter.lawRef.mudadDeadline', 'KSA employers have 30 days from the wage due date to upload wages to Mudad. Missed deadlines downgrade Nitaqat band, block Iqama renewals, and record a non-compliance violation.'),
                              }
                            : {
                                code: 'MOHRE Ministerial Decree 43/2022',
                                description: t('payroll.complianceCenter.lawRef.wps', 'Wages must be paid through the MOHRE-approved Wages Protection System within 15 days of the due date. Late submission triggers escalating penalties.'),
                              },
                        ]}
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {deadline.isSubmitted
                        ? (isKsa ? t('payroll.complianceCenter.wps.ksaUploaded', 'Uploaded to Mudad') : t('payroll.wps.submitted', 'Submitted'))
                        : `${deadline.daysElapsed} ${t('payroll.wps.daysElapsed', 'days elapsed')} · ${deadline.daysRemaining} ${t('payroll.wps.daysRemaining', 'days remaining')}`}
                    </p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${zoneColors[deadline.zone] ?? zoneColors.Green}`}>
                  {deadline.isSubmitted
                    ? (isKsa ? t('payroll.complianceCenter.wps.ksaUploadedBadge', 'Uploaded') : t('payroll.wps.submittedBadge', 'Submitted'))
                    : deadline.zone}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {isUae && !loading && !deadline && (
          <p className="text-sm text-muted-foreground">
            {t('payroll.complianceCenter.wps.noDeadline', 'No WPS deadline data for this period.')}
          </p>
        )}

        {isUae && deadline && (
          <MohreTimeline currentDay={deadline.daysElapsed} deadlineDays={deadline.deadlineDays} isSubmitted={deadline.isSubmitted} />
        )}
        {isKsa && deadline && (
          <MudadTimeline currentDay={deadline.daysElapsed} deadlineDays={deadline.deadlineDays} isSubmitted={deadline.isSubmitted} isRunPayrollMode={deadline.isRunPayrollMode ?? false} />
        )}

        {/* Generate panel */}
        <div className="rounded-lg border border-border bg-card p-4 flex items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-foreground">
              {isKsa
                ? t('payroll.complianceCenter.ksaWps.generateMudad', 'Generate Mudad File')
                : t('Generate SIF for {{month}} {{year}}', { month: monthName, year })}
            </p>
            <p className="text-sm text-muted-foreground">
              {noPayrollForPeriod
                ? t('payroll.complianceCenter.ksaWps.noPayrollHint', 'No payroll exists for {{month}} {{year}} yet. Generate it in the Payroll Table first, then come back here.', { month: monthName, year })
                : preflight?.canGenerate
                ? (warningIssues.length > 0
                    ? t('The file will still generate. Resolve {{n}} coverage warning(s) before the deadline.', { n: warningIssues.length })
                    : t('All employees pass the pre-flight checks for this period.'))
                : t('Resolve {{n}} pre-flight issues before submitting to the bank.', { n: blockingIssues.length })}
            </p>
            {noPayrollForPeriod && (
              <Link to="/payroll/table" className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                {t('payroll.complianceCenter.ksaWps.goToPayrollTable', 'Go to Payroll Table')}
                <ArrowRight className="w-3 h-3" />
              </Link>
            )}
            {preflight && !preflight.canGenerate && (
              <ul className="mt-2 text-xs text-muted-foreground space-y-0.5">
                {ibanIssues > 0 && <li>{t('IBAN missing or invalid')}: {ibanIssues}</li>}
                {labourCardIssues > 0 && <li>{t('Labour card missing')}: {labourCardIssues}</li>}
                {otherIssues > 0 && <li>{t('Other issues')}: {otherIssues}</li>}
              </ul>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isKsa && latestKsaForPeriod?.status === 'Generated' && (
              <Button variant="outline" size="sm" onClick={() => setMarkOpen(true)}>
                {t('payroll.complianceCenter.ksaWps.markUploaded', 'Mark Uploaded to Mudad')}
              </Button>
            )}
            <Button
              onClick={handleGenerate}
              disabled={generating || noPayrollForPeriod || (preflight !== null && !preflight.canGenerate)}
              loading={generating}
              iconName="Download"
              data-testid="generate-sif-btn"
            >
              {isKsa
                ? t('payroll.complianceCenter.ksaWps.generateMudad', 'Generate Mudad File')
                : t('Generate SIF')}
            </Button>
          </div>
        </div>

        {/* Company setup pre-validation (with Fix this → links) */}
        {preflight && preflight.companyChecks.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold">
                  {t('payroll.complianceCenter.wps.companySetupHeader', 'Company Setup Pre-Validation')}
                </h3>
                {preflight.companyPassed ? (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    {t('payroll.complianceCenter.wps.allPassed', 'All passed')}
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {t('payroll.complianceCenter.wps.needsAttention', 'Needs attention')}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                {t('payroll.complianceCenter.wps.companySetupSubtitle', 'Configuration the SIF needs at the company level. Each failed check links to where you can fix it.')}
              </p>
              <ul className="divide-y divide-border">
                {preflight.companyChecks.map((check, i) => {
                  const fixTo = sifCheckFixPath(check.code);
                  return (
                  <li key={check.code || i} className="py-3">
                    <div className="flex items-start gap-3">
                      {check.passed ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <p className={`text-sm font-medium ${check.passed ? '' : 'text-red-700'}`}>
                            {t(check.label, check.label)}
                          </p>
                          {check.detail && (
                            <span className="text-xs font-mono text-muted-foreground">{check.detail}</span>
                          )}
                        </div>
                        {!check.passed && check.hint && (
                          <p className="text-xs text-muted-foreground mt-1">{t(check.hint, check.hint)}</p>
                        )}
                        {!check.passed && fixTo && (
                          <Link to={fixTo} className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                            {t('payroll.complianceCenter.wps.fixThis', 'Fix this')}
                            <ArrowRight className="w-3 h-3" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Coverage warnings — non-blocking (e.g. CCHI gaps); the wage file still generates. */}
        {preflight && warningIssues.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold">
                  {t('payroll.complianceCenter.wps.coverageWarningsHeader', 'Coverage Warnings')}
                </h3>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {t('payroll.complianceCenter.wps.wontBlock', "Won't block filing")}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                {t('payroll.complianceCenter.wps.coverageWarningsSubtitle', 'The wage file will still generate. Resolve these before the bank deadline.')}
              </p>
              <ul className="divide-y divide-border">
                {warningIssues.map((issue, i) => (
                  <li key={(issue.userId + issue.issue) || i} className="py-3">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-amber-700">{issue.employeeName}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {/cchiExpired/i.test(issue.issue)
                            ? t('payroll.complianceCenter.wps.cchiExpired', 'CCHI health-insurance policy has expired.')
                            : t('payroll.complianceCenter.wps.cchiMissing', 'No in-force CCHI health-insurance policy (required for non-Saudis).')}
                        </p>
                        <Link to={`/profile/${encodeURIComponent(issue.userId)}/payroll/benefits#cchi`} className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                          {t('payroll.complianceCenter.wps.fixInCchi', 'Fix in CCHI')}
                          <ArrowRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Per-employee blocking issues — warnings (e.g. CCHI) live in the Coverage Warnings card above. */}
        {preflight && blockingIssues.length > 0 && (
          <div id="employee-issues" className="scroll-mt-24">
            <Card className="border-red-200">
              <CardContent className="p-4">
                <h3 className="font-semibold mb-1 flex items-center gap-2 text-red-700">
                  <XCircle className="w-4 h-4 text-red-500" />
                  {t('payroll.complianceCenter.wps.employeeIssuesHeader', 'Employee Pre-Validation Issues')} ({blockingIssues.length})
                </h3>
                <p className="text-xs text-muted-foreground mb-3">
                  {t('payroll.complianceCenter.wps.employeeIssuesSubtitle', "Employees missing the data the SIF requires. Use “Fix this” to jump to the employee's profile and clear each one.")}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-muted-foreground">
                        <th className="text-start py-2 font-medium">{t('common.employee', 'Employee')}</th>
                        <th className="text-start py-2 font-medium">{t('common.issue', 'Issue')}</th>
                        <th className="text-start py-2 font-medium">{t('common.field', 'Field')}</th>
                        <th className="text-end py-2 font-medium">{t('common.action', 'Action')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {blockingIssues.map((issue, i) => {
                        const fixTo = sifEmployeeIssueFixPath(issue.issue, issue.userId);
                        return (
                          <tr key={i} className="border-b border-border last:border-0">
                            <td className="py-2 font-medium">
                              {fixTo ? (
                                <Link to={fixTo} className="text-primary hover:underline">
                                  {issue.employeeName}
                                </Link>
                              ) : (
                                issue.employeeName
                              )}
                            </td>
                            <td className="py-2 text-red-600">
                              {t(`payroll.complianceCenter.ksaWps.issue.${issue.issue}`, issue.issue)}
                            </td>
                            <td className="py-2 font-mono text-xs">{issue.field}</td>
                            <td className="py-2 text-end">
                              {fixTo && (
                                <Link
                                  to={fixTo}
                                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                >
                                  {t('payroll.complianceCenter.wps.fixThis', 'Fix this')}
                                  <ArrowRight className="w-3 h-3" />
                                </Link>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* History — UAE SIF table */}
        {isUae && (
          <div className="rounded-lg border border-border" data-testid="wps-history-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('Period')}</TableHead>
                  <TableHead>{t('Generated On')}</TableHead>
                  <TableHead>{t('Generated By')}</TableHead>
                  <TableHead className="text-end">{t('Employees')}</TableHead>
                  <TableHead className="text-end">{t('Total Amount')}</TableHead>
                  <TableHead>{t('Status')}</TableHead>
                  <TableHead>{t('Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8"><Spinner size="md" /></TableCell>
                  </TableRow>
                )}
                {!loading && filteredHistory.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground text-sm">
                      {t('No SIF files generated yet.')}
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredHistory.map(h => (
                  <TableRow key={h.id} data-testid={`wps-row-${h.id}`}>
                    <TableCell className="font-medium">{`${String(h.month).padStart(2, '0')}/${h.year}`}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{appDateTime(parseApiUtc(h.generatedOn))}</TableCell>
                    <TableCell>{h.generatedByName}</TableCell>
                    <TableCell className="text-end">{h.employeeCount}</TableCell>
                    <TableCell className="text-end">{formatNumber(h.totalAmount)}</TableCell>
                    <TableCell>
                      <StatusBadge variant={statusVariant[h.status] ?? 'neutral'} size="sm">{t(h.status)}</StatusBadge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleRedownload(h)} iconName="Download">
                          {t('Re-download')}
                        </Button>
                        {h.status === 'Generated' && (
                          <Button variant="outline" size="sm" onClick={() => handleMarkSubmitted(h)} loading={marking === h.id} disabled={marking === h.id} iconName="Check">
                            {t('Mark Submitted')}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* History — KSA Mudad table */}
        {isKsa && (
          <div data-testid="wps-history-table">
            <KsaMudadHistory history={ksaHistory} />
          </div>
        )}

        {isKsa && (
          <MarkUploadedModal
            open={markOpen}
            year={year}
            month={month}
            onClose={() => setMarkOpen(false)}
            onSuccess={async () => {
              await loadKsaHistory();
              await loadPeriodInfo();
            }}
          />
        )}

        <Dialog open={preflightPopupOpen} onOpenChange={setPreflightPopupOpen}>
          <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-primary" />
                {t('payroll.wps.preflightChecklist', 'Pre-flight Checklist')}
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              {preflight && (
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold mb-2">{t('payroll.wps.companyChecksTitle', 'Company Checks')}</h4>
                    {preflight.companyChecks.length === 0 ? (
                      <p className="text-sm text-muted-foreground">{t('payroll.wps.noCompanyChecks', 'No company-level checks.')}</p>
                    ) : (
                      <ul className="space-y-2">
                        {preflight.companyChecks.map((check, i) => (
                          <li key={check.code || i} className="flex items-start gap-2">
                            {check.passed ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                            ) : check.isWarning ? (
                              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                            )}
                            <div>
                              <p className={`text-sm ${check.passed ? 'text-foreground' : check.isWarning ? 'text-amber-700' : 'text-red-700'}`}>
                                {t(check.label, check.label)}
                              </p>
                              {check.detail && (
                                <p className="text-xs text-muted-foreground">{check.detail}</p>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {blockingIssues.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold mb-2 text-red-700">
                        {t('payroll.wps.blockingIssuesTitle', 'Blocking Issues')} ({blockingIssues.length})
                      </h4>
                      <ul className="space-y-1.5">
                        {blockingIssues.map((issue, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm text-red-700">{issue.employeeName}</p>
                              <p className="text-xs text-muted-foreground">
                                {t(`payroll.complianceCenter.ksaWps.issue.${issue.issue}`, issue.issue)}
                              </p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {warningIssues.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold mb-2 text-amber-700">
                        {t('payroll.wps.warningIssuesTitle', 'Coverage Warnings')} ({warningIssues.length})
                      </h4>
                      <ul className="space-y-1.5">
                        {warningIssues.map((issue, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm text-amber-700">{issue.employeeName}</p>
                              <p className="text-xs text-muted-foreground">
                                {t(`payroll.complianceCenter.ksaWps.issue.${issue.issue}`, issue.issue)}
                              </p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {blockingIssues.length === 0 && warningIssues.length === 0 && preflight.companyPassed && (
                    <div className="flex items-center gap-2 text-emerald-700 text-sm">
                      <CheckCircle2 className="w-4 h-4" />
                      {t('payroll.wps.allClear', 'All checks passed. Ready to generate.')}
                    </div>
                  )}
                </div>
              )}
            </DialogBody>
            <DialogFooter className="justify-center">
              <Button onClick={() => setPreflightPopupOpen(false)}>
                {t('common.ok', 'OK')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={fileWarningOpen} onOpenChange={setFileWarningOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                {t('payroll.complianceCenter.ksaWps.fileWarningTitle', 'File Generated Successfully')}
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              <p className="text-sm text-muted-foreground">
                {t('payroll.complianceCenter.ksaWps.fileWarningBody',
                  'If you open this file in Excel, dates may be reformatted (e.g. 2026-06-01 becomes 6/1/2026) and numeric fields may change appearance. If you save after opening, these changes could make the file incompatible with Mudad.')}
              </p>
              <p className="text-sm font-medium mt-3">
                {t('payroll.complianceCenter.ksaWps.fileWarningAdvice',
                  'If the file will be opened and edited, double check the changes before uploading to Mudad.')}
              </p>
            </DialogBody>
            <DialogFooter className="justify-center">
              <Button onClick={() => setFileWarningOpen(false)}>
                {t('common.ok', 'OK')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </div>
    </PageLayout>
  );
};

interface SummaryCardProps {
  icon: React.ComponentType<{ className?: string; size?: number }>;
  tone: 'ok' | 'warn' | 'error';
  label: string;
  value: string;
  testId?: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ icon: Icon, tone, label, value, testId }) => {
  const tones = {
    ok: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-700',
    warn: 'border-amber-500/30 bg-amber-500/5 text-amber-700',
    error: 'border-destructive/30 bg-destructive/5 text-destructive',
  } as const;
  return (
    <div className={`rounded-lg border px-3 py-2 flex items-center gap-2 ${tones[tone]}`} data-testid={testId}>
      <Icon size={18} />
      <div className="leading-tight">
        <p className="text-[11px] uppercase tracking-wide opacity-70">{label}</p>
        <p className="text-sm font-semibold">{value}</p>
      </div>
    </div>
  );
};

export default WpsCenter;
